import React from 'react';
import { Phone, Mic, HeadphonesIcon, Settings, Activity, ChevronLeft, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function VoiceService() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700">
              <ChevronLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <a 
              href="https://wa.me/60177751631"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-green-600 hover:text-green-700"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Contact on WhatsApp</span>
            </a>
          </div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <Phone className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">AI Voice Agent Solutions</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Transform your customer service with intelligent voice agents that handle calls naturally and efficiently.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80"
              alt="Voice Agent Technology"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Advanced Voice Technology</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Natural voice synthesis</li>
                <li>• Real-time voice recognition</li>
                <li>• Emotion detection</li>
                <li>• Multi-language support</li>
                <li>• Call quality optimization</li>
              </ul>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-6">Key Features</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <Mic className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">Natural Voice Interaction</h3>
                  <p className="text-gray-600">Human-like conversations with advanced speech recognition and synthesis.</p>
                </div>
              </div>
              <div className="flex items-start">
                <HeadphonesIcon className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">24/7 Call Handling</h3>
                  <p className="text-gray-600">Round-the-clock availability for customer support and inquiries.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Settings className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">Custom Integration</h3>
                  <p className="text-gray-600">Seamless integration with your existing phone systems and CRM.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Activity className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">Performance Analytics</h3>
                  <p className="text-gray-600">Detailed insights into call handling and customer satisfaction.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-8">Voice Agent Comparison</h2>
          <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-indigo-600">Our AI Voice Agent</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">ElevenLabs</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Others</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Starting Price</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">5¢ per minute</td>
                  <td className="px-6 py-4 text-center text-sm">30¢ per minute</td>
                  <td className="px-6 py-4 text-center text-sm">25-50¢ per minute</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Real-time Processing</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes</td>
                  <td className="px-6 py-4 text-center text-sm">Limited</td>
                  <td className="px-6 py-4 text-center text-sm">Varies</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Custom Voice Training</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes (Included)</td>
                  <td className="px-6 py-4 text-center text-sm">Yes (Additional Cost)</td>
                  <td className="px-6 py-4 text-center text-sm">Limited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Emotion Detection</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes</td>
                  <td className="px-6 py-4 text-center text-sm">No</td>
                  <td className="px-6 py-4 text-center text-sm">Limited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Integration Support</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Full Support</td>
                  <td className="px-6 py-4 text-center text-sm">API Only</td>
                  <td className="px-6 py-4 text-center text-sm">Varies</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Deployment Assistance</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes</td>
                  <td className="px-6 py-4 text-center text-sm">No</td>
                  <td className="px-6 py-4 text-center text-sm">Limited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">24/7 Support</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes</td>
                  <td className="px-6 py-4 text-center text-sm">No</td>
                  <td className="px-6 py-4 text-center text-sm">Varies</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-center">
          <Link 
            to="/#contact"
            className="inline-flex items-center bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            Get Started with Voice Agent
          </Link>
        </div>
      </div>
    </div>
  );
}